import i18next from 'i18next';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchAppointmentSessionNote,
  fetchDeviceSettingsHistory,
  fetchLastDeviceSettings,
  fetchPatientPerformedTreatments,
  fetchPatientSessionNotes,
  fetchSessionNote,
} from '@/api/sessionNotes/sessionNotesApi';
import type {
  DeviceSetting,
  DeviceSettingsHistoryEntry,
  PerformedTreatment,
  SessionNote,
  SessionNoteSummary,
} from '@/api/sessionNotes/sessionNotesApi.types';
import type { PaginatedResult } from '@/api/pagination.types';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import {
  buildAppointmentSessionNoteQueryKey,
  buildDeviceSettingsHistoryQueryKey,
  buildLastDeviceSettingsQueryKey,
  buildPatientPerformedTreatmentsQueryKey,
  buildPatientSessionNotesQueryKey,
  buildSessionNoteQueryKey,
} from '@/constants/queryKeys';

export function usePatientSessionNotesQuery(
  patientId: string,
  page: number,
  status: string | null
): UseQueryResult<PaginatedResult<SessionNoteSummary>, Error> {
  return useQuery({
    queryKey: [
      ...buildPatientSessionNotesQueryKey(patientId),
      'list',
      i18next.language,
      page,
      status,
    ],
    queryFn: function loadNotes() {
      return fetchPatientSessionNotes(patientId, page, ADMIN_LIST_PAGE_SIZE, status);
    },
    enabled: patientId.length > 0,
    placeholderData: keepPreviousData,
  });
}

export function usePatientPerformedTreatmentsQuery(
  patientId: string,
  page: number
): UseQueryResult<PaginatedResult<PerformedTreatment>, Error> {
  return useQuery({
    queryKey: [...buildPatientPerformedTreatmentsQueryKey(patientId), i18next.language, page],
    queryFn: function loadPerformedTreatments() {
      return fetchPatientPerformedTreatments(patientId, page, ADMIN_LIST_PAGE_SIZE);
    },
    enabled: patientId.length > 0,
    placeholderData: keepPreviousData,
  });
}

export function useSessionNoteQuery(noteId: string): UseQueryResult<SessionNote, Error> {
  return useQuery({
    queryKey: [...buildSessionNoteQueryKey(noteId), i18next.language],
    queryFn: function loadNote() {
      return fetchSessionNote(noteId);
    },
    enabled: noteId.length > 0,
  });
}

export function useAppointmentSessionNoteQuery(
  appointmentId: string,
  enabled: boolean
): UseQueryResult<SessionNote, Error> {
  return useQuery({
    queryKey: [...buildAppointmentSessionNoteQueryKey(appointmentId), i18next.language],
    queryFn: function loadNote() {
      return fetchAppointmentSessionNote(appointmentId);
    },
    enabled: enabled && appointmentId.length > 0,
  });
}

export function useLastDeviceSettingsQuery(
  patientId: string,
  treatmentId: string,
  enabled: boolean
): UseQueryResult<DeviceSetting[], Error> {
  return useQuery({
    queryKey: buildLastDeviceSettingsQueryKey(patientId, treatmentId),
    queryFn: function loadSettings() {
      return fetchLastDeviceSettings(patientId, treatmentId);
    },
    enabled: enabled && patientId.length > 0 && treatmentId.length > 0,
  });
}

export function usePatientDeviceSettingsHistoryQuery(
  patientId: string
): UseQueryResult<DeviceSettingsHistoryEntry[], Error> {
  return useQuery({
    queryKey: [...buildDeviceSettingsHistoryQueryKey(patientId), i18next.language],
    queryFn: function loadHistory() {
      return fetchDeviceSettingsHistory(patientId);
    },
    enabled: patientId.length > 0,
  });
}
